import React, { useRef, useEffect } from "react";
import Chart from "chart.js/auto";
import "./FullscreenChart.css";

// Функция вычисления конфигурации графика физического износа
function getWearChartConfig(yearBuilt, series) {
  const serviceLifeBySeries = {
    "1-447": 100,
    "1-447М": 100,
    "1-447-С": 100,
    'Индивидуальный проект ("сталинка")': 125,
    "1-464М": 40,
    "1-464": 40,
    "1-464-Д-82": 40,
    "111-84": 80,
    "111-84, КОПЭН": 80,
    "111-84М": 80,
    "111-112": 80,
    "111-112 - Основа, К-69 - Вставка": 80,
    "НК-12": 80,
    "К-69": 80,
  };
  const serviceLife = serviceLifeBySeries[series] || 100;
  const years = [];
  const wearPercentages = [];
  const wearPercentagesModified = [];
  const includeModifiedLine = ["1-447", "1-447М", "1-447-С", 'Индивидуальный проект ("сталинка")'].includes(series);
  let age = 0;
  let wearPercentage = 0;
  let wearPercentageModified = 0;
  const startModifiedYear = 1985;
  let mainLineReached100 = false;
  let modifiedLineReached100 = false;
  while (!mainLineReached100 || (includeModifiedLine && !modifiedLineReached100)) {
    const currentYear = yearBuilt + age;
    wearPercentage = (age / serviceLife) * 100;
    wearPercentages.push(Math.min(wearPercentage, 100));
    if (wearPercentage >= 100) {
      mainLineReached100 = true;
    }
    if (includeModifiedLine && currentYear >= startModifiedYear) {
      if (!modifiedLineReached100) {
        if (currentYear === startModifiedYear) {
          wearPercentageModified = wearPercentage;
        } else {
          wearPercentageModified += (1 / serviceLife) * 100 * 0.8;
        }
        wearPercentagesModified.push(Math.min(wearPercentageModified, 100));
        if (wearPercentageModified >= 100) {
          modifiedLineReached100 = true;
        }
      } else {
        wearPercentagesModified.push(100);
      }
    } else if (includeModifiedLine) {
      wearPercentagesModified.push(null);
    }
    years.push(currentYear);
    if (mainLineReached100 && (!includeModifiedLine || modifiedLineReached100)) break;
    age++;
    if (age > 150) break;
  }
  return {
    data: {
      labels: years,
      datasets: [
        {
          label: "Физический износ (%)",
          data: wearPercentages,
          borderColor: "rgba(27, 194, 152, 1)",
          backgroundColor: "rgba(27, 194, 152, 0.2)",
          fill: true,
          pointRadius: 2,
          borderWidth: 1,
        },
        ...(includeModifiedLine
          ? [
              {
                label: "Износ при реконструкции (%)",
                data: wearPercentagesModified,
                borderColor: "rgba(54, 162, 235, 1)",
                backgroundColor: "rgba(54, 162, 235, 0.2)",
                fill: true,
                pointRadius: 2,
                borderWidth: 2,
              },
            ]
          : []),
      ],
    },
    options: {
      responsive: true,
      scales: {
        y: {
          max: 100,
          title: { display: true, text: "Износ (%)" },
        },
        x: {
          title: { display: true, text: "Год" },
        },
      },
      plugins: { legend: { display: true, position: "top" } },
    },
  };
}

// Функция вычисления конфигурации графика морального износа
function getMoralChartConfig(yearBuilt, series) {
  const years = [];
  const moralDepreciation = [];
  let moralPercentage = 0;
  const specialSeriesWithLine = ["1-447", "1-447М", "1-447-С", 'Индивидуальный проект ("сталинка")'];
  const seriesReaches100In40Years = ["1-464", "1-464-Д-82", "1-464М"];
  const isSeries1447 = ["1-447", "1-447М", "1-447-С"].includes(series);
  const isStalinka = series === 'Индивидуальный проект ("сталинка")';
  const isSeries464 = seriesReaches100In40Years.includes(series);
  const includeConstantLine = specialSeriesWithLine.includes(series);
  let valueAt2024 = null;
  for (let age = 0; ; age++) {
    const currentYear = yearBuilt + age;
    if (isSeries1447) {
      if (currentYear >= 1960 && currentYear < 1972) {
        moralPercentage += 0.8;
      } else if (currentYear === 1972) {
        moralPercentage += 20;
      } else {
        moralPercentage += 0.9;
      }
    } else if (isStalinka) {
      if (currentYear >= 1950 && currentYear < 1995) {
        moralPercentage += 0.7;
      } else if (currentYear === 1995) {
        moralPercentage += 10;
      } else {
        moralPercentage += 0.8;
      }
    } else if (isSeries464) {
      moralPercentage = age < 40 ? age * 2.5 : 100;
    } else {
      moralPercentage += 0.8;
    }
    moralPercentage = Math.min(moralPercentage, 100);
    moralDepreciation.push(moralPercentage);
    years.push(currentYear);
    if (includeConstantLine && currentYear === 2024) {
      valueAt2024 = moralPercentage;
    }
    if (moralPercentage === 100 && !isSeries464) break;
    if (age > 150) break;
  }
  const datasets = [
    {
      label: "Моральный износ (%)",
      data: moralDepreciation,
      borderColor: "rgba(255, 99, 132, 1)",
      backgroundColor: "rgba(255, 99, 132, 0.2)",
      fill: true,
      pointRadius: 2,
      borderWidth: 1,
    },
  ];
  if (includeConstantLine && valueAt2024 !== null) {
    const constantLineData = years.map((year) => (year >= 2024 ? valueAt2024 : null));
    datasets.push({
      label: "Моральный износ при реконструкции",
      data: constantLineData,
      borderColor: "rgba(54, 162, 235, 1)",
      borderDash: [5, 5],
      fill: false,
      pointRadius: 0,
      borderWidth: 1,
    });
  }
  return {
    data: { labels: years, datasets: datasets },
    options: {
      responsive: true,
      scales: {
        y: { max: 100, title: { display: true, text: "Износ (%)" } },
        x: { title: { display: true, text: "Год" } },
      },
      plugins: { legend: { display: true, position: "top" } },
    },
  };
}

// Компонент GraphChart – отрисовывает график на canvas
// Пропсы:
//   chartMode: "wear" или "moral"
//   yearBuilt, series: данные для расчёта графика
//   inline: true для встроенного отображения, false для полноэкранного (стили через CSS)
//   darkMode: включена ли темная тема
export const GraphChart = ({ chartMode, yearBuilt, series, inline = true, darkMode = false }) => {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (yearBuilt && series && canvasRef.current) {
      let config;
      if (chartMode === "wear") {
        config = getWearChartConfig(yearBuilt, series);
      } else if (chartMode === "moral") {
        config = getMoralChartConfig(yearBuilt, series);
      }
      
      if (config) {
        // Улучшения для полноэкранного режима
        if (!inline) {
          // Увеличиваем размер шрифтов и линий для большей четкости
          config.options = {
            ...config.options,
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              ...config.options.scales,
              y: {
                ...config.options.scales.y,
                ticks: {
                  color: darkMode ? '#ddd' : '#333',
                  font: {
                    size: 14,
                    weight: 'bold'
                  }
                },
                grid: {
                  color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  lineWidth: 1
                },
                title: {
                  display: true,
                  text: 'Износ (%)',
                  font: {
                    size: 16,
                    weight: 'bold'
                  },
                  color: darkMode ? '#ddd' : '#333'
                }
              },
              x: {
                ...config.options.scales.x,
                ticks: {
                  color: darkMode ? '#ddd' : '#333',
                  font: {
                    size: 14,
                    weight: 'bold'
                  },
                  maxRotation: 0,
                  autoSkip: true,
                  autoSkipPadding: 40
                },
                grid: {
                  color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  lineWidth: 1
                },
                title: {
                  display: true,
                  text: 'Год',
                  font: {
                    size: 16,
                    weight: 'bold'
                  },
                  color: darkMode ? '#ddd' : '#333'
                }
              }
            },
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: {
                  font: {
                    size: 14,
                    weight: 'bold'
                  },
                  padding: 20,
                  usePointStyle: true,
                  color: darkMode ? '#ddd' : '#333'
                }
              },
              tooltip: {
                backgroundColor: darkMode ? 'rgba(40, 40, 40, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                titleColor: darkMode ? '#ddd' : '#333',
                bodyColor: darkMode ? '#ddd' : '#333',
                borderColor: darkMode ? '#555' : '#ccc',
                borderWidth: 1,
                titleFont: {
                  size: 16,
                  weight: 'bold'
                },
                bodyFont: {
                  size: 14
                },
                padding: 12,
                boxWidth: 10,
                boxHeight: 10,
                boxPadding: 3,
                usePointStyle: true
              }
            }
          };
          
          // Увеличиваем толщину линий для лучшей видимости
          config.data.datasets = config.data.datasets.map(dataset => ({
            ...dataset,
            borderWidth: dataset.borderWidth + 1,
            pointRadius: 4,
            pointHoverRadius: 7,
            tension: 0.3 // Плавные линии
          }));
        }
        
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }
        chartInstanceRef.current = new Chart(canvasRef.current, {
          type: "line",
          data: config.data,
          options: config.options,
        });
      }
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartMode, yearBuilt, series, inline, darkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={inline ? { maxWidth: "750px", maxHeight: "500px" } : { width: "100%", height: "100%" }}
      width={inline ? "750" : "1200"}
      height={inline ? "500" : "800"}
    />
  );
};

// Компонент FullscreenChart – отображает график в полноэкранном режиме
const FullscreenChart = ({
  visible,
  onClose,
  chartMode, // "wear" или "moral"
  yearBuilt,
  series,
  darkMode = false
}) => {

  useEffect(() => {
    if (visible) {
      // Предотвращаем прокрутку страницы
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [visible]);

  if (!visible) return null;
  
  // Формируем заголовок в зависимости от типа графика
  const title = chartMode === "wear" 
    ? "График физического износа" 
    : "График морального износа";
  
  return (
    <div className={`fullscreen-chart ${darkMode ? 'fullscreen-chart-dark' : ''}`}>
      {darkMode && <div className="glow-center"></div>}
      <div className={`fullscreen-chart-content ${darkMode ? 'fullscreen-chart-content-dark' : ''}`}>
        <h2 className="fullscreen-chart-title">{title}</h2>
        <p className="fullscreen-chart-subtitle">
          Серия дома: <strong>{series}</strong>, 
          Год постройки: <strong>{yearBuilt}</strong>
        </p>
        <div className={`fullscreen-chart-wrapper ${darkMode ? 'fullscreen-chart-wrapper-dark' : ''}`}>
          <GraphChart chartMode={chartMode} yearBuilt={yearBuilt} series={series} inline={false} darkMode={darkMode} />
        </div>
      </div>
      
      <button className={`close-chart-button ${darkMode ? 'close-chart-button-dark' : ''}`} onClick={onClose}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18" stroke={darkMode ? "#ffffff" : "#000000"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 6L18 18" stroke={darkMode ? "#ffffff" : "#000000"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

export default FullscreenChart;
